import { observer } from 'mobx-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { isDev } from '../../core/api';
import { Loader } from '../loader';

import './button.scss';

type ButtonHover = 'scale' | 'primary-invert';
type ButtonColor =
    | 'primary'
    | 'primary-invert'
    | 'secondary'
    | 'grey'
    | 'transparent'
    | 'transparent-invert';

interface ButtonCommonProps {
    children?: React.ReactNode;
    fullwidth?: boolean;
    color: ButtonColor;
    hover?: ButtonHover;
    loading?: boolean;
    className?: string;
    'data-role'?: string;
}

interface ButtonClickableProps extends ButtonCommonProps {
    onClick?(): void;
}

interface ButtonRedirectProps extends ButtonCommonProps {
    to?: string;
    href?: string;
}

const isButtonRedirect = (
    props: ButtonClickableProps | ButtonRedirectProps
): props is ButtonRedirectProps => 'to' in props;

const isButtonLink = (
    props: ButtonClickableProps | ButtonRedirectProps
): props is ButtonRedirectProps => 'href' in props;

export const Button = observer(function Button(
    props: ButtonClickableProps | ButtonRedirectProps
) {
    const {
        className,
        children,
        fullwidth,
        color,
        hover,
        loading,
        ...restProps
    } = props;

    const search = location.search;

    if (isDev()) {
        if (hover === 'primary-invert' && color !== 'primary-invert') {
            throw new Error(
                'Button error: if hover is primary-invert, color also needs to be primary-invert'
            );
        }
    }

    const classNames = classnames(className, 'buttonc', {
        'buttonc--fullwidth': fullwidth,
        'buttonc--loading': loading,

        'buttonc--color-primary': color === 'primary',
        'buttonc--color-primary-invert': color === 'primary-invert',
        'buttonc--color-secondary': color === 'secondary',
        'buttonc--color-grey': color === 'grey',
        'buttonc--color-transparent': color === 'transparent',
        'buttonc--color-transparent-invert': color === 'transparent-invert',

        'buttonc--hover-scale': hover === 'scale',
        'buttonc--hover-primary-invert':
            color === 'primary-invert' && hover === 'primary-invert'
    });

    const content = (
        <button className={classNames} type="button" {...restProps}>
            <span>{children}</span>
            {loading && <Loader color="inherit" size="contain" />}
        </button>
    );

    if (isButtonRedirect(props) && props.to) {
        return <Link to={`${props.to}${search}`}>{content}</Link>;
    }

    if (isButtonLink(props) && props.href) {
        return (
            <a href={`${props.href}${search}`} target="_blank" rel="noreferrer">
                {content}
            </a>
        );
    }

    return content;
});

interface ButtonCleanProps {
    children?: React.ReactNode;
    className?: string;
    onClick(): void;
    'data-role'?: string;
}

export const ButtonClean = observer(function ButtonClean(
    props: ButtonCleanProps
) {
    const { children, className, ...restProps } = props;
    const classNames = classnames('buttonc-clean', className);

    return (
        <span className={classNames} {...restProps}>
            {children}
        </span>
    );
});
